import cx from 'classnames'
import React from 'react'
import styled, { css } from 'styled-components'
import Checkbox, {
  CheckBoxSelector,
} from 'uiComponents/atoms/Checkbox/Checkbox'
import Ripple from 'uiComponents/atoms/Ripple/Ripple'
import Tooltip from 'uiComponents/atoms/Tooltip/Tooltip'
import {
  backgroundColor,
  complementaryColor,
  inactiveColor,
} from 'uiComponents/constants/colors'
import {
  fontFamilyPrimary,
  fsXS,
  fwHeavy,
} from 'uiComponents/constants/typography'

interface IContainerProps {
  selected: boolean
  disabled: boolean
  dataTestId?: string
}

const disabledStyle = css`
  text-decoration: line-through;
  color: ${inactiveColor};

  &:hover {
    background-color: ${complementaryColor};
  }
`
const selectedStyle = css`
  ${fwHeavy};
`

const Container = styled.li<IContainerProps>`
  display: flex;
  align-items: center;
  height: 32px;
  ${fontFamilyPrimary};
  font-size: ${fsXS}px;
  line-height: 32px;
  cursor: pointer;
  background-color: ${complementaryColor};
  padding: 0 15px;

  &::selection {
    background: transparent;
  }

  &.highlighted,
  &:focus,
  &:hover {
    background-color: ${backgroundColor};
  }

  ${props => css`
    ${props.disabled && disabledStyle};
    ${props.selected && selectedStyle};
  `};

  ${CheckBoxSelector} {
    margin-right: 10px;
  }
`

interface IDropdownOption {
  selected: boolean
  disabled?: boolean
  tooltipLabel?: string
  withCheckbox?: boolean
  onClick?: React.MouseEventHandler
  className?: string
  style?: React.CSSProperties
  highlighted?: boolean
  [key: string]: any
}

const OptionContainer: React.FC<IDropdownOption> = ({
  withCheckbox,
  selected,
  disabled,
  children,
  tooltipLabel,
  onClick,
  className,
  style,
  highlighted,
  ...rest
}) => {
  const dataTestId = disabled
    ? 'DropDownItem_Disabled'
    : selected
    ? 'DropdownItem_Selected'
    : 'DropdownItem_Active'

  return (
    <Ripple
      data-testid={dataTestId}
      as={Container}
      selected={selected}
      disabled={disabled}
      onClick={onClick}
      className={cx(className, { highlighted })}
      style={style}
      {...rest}
    >
      {withCheckbox && <Checkbox withoutRipple checked={selected} />}

      {tooltipLabel ? (
        <Tooltip label={tooltipLabel} ariaLabel={tooltipLabel}>
          {React.isValidElement(children) ? children : <div>{children}</div>}
        </Tooltip>
      ) : (
        children
      )}
    </Ripple>
  )
}

export default OptionContainer
